import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore'
import { db } from './config'

// User Profile Types
export interface UserProfile {
  walletAddress: string
  username?: string
  twitterHandle?: string
  twitterLinked: boolean
  createdAt: Timestamp
  lastLoginAt: Timestamp
  gamesPlayed: number
  totalWins: number
  totalLosses: number
  highestNetWorth: number
  preferences: UserPreferences
}

export interface UserPreferences {
  crtEffectEnabled: boolean
  soundEnabled: boolean
  notificationsEnabled: boolean
}

export interface GameSummary {
  id: string
  status: 'active' | 'won' | 'lost' | 'abandoned'
  startedAt: Timestamp
  endedAt?: Timestamp
  startingToken: string
  startingAmount: number
  finalAmount?: number
  percentageChange?: number
  daysPlayed: number
  difficulty: string
}

const DEFAULT_PREFERENCES: UserPreferences = {
  crtEffectEnabled: true,
  soundEnabled: true,
  notificationsEnabled: true,
}

/**
 * User Profile Service
 * Manages user data in Firestore
 */
export class UserProfileService {
  private static instance: UserProfileService

  private constructor() {}

  static getInstance(): UserProfileService {
    if (!UserProfileService.instance) {
      UserProfileService.instance = new UserProfileService()
    }
    return UserProfileService.instance
  }

  /**
   * Get user profile by wallet address
   */
  async getProfile(walletAddress: string): Promise<UserProfile | null> {
    const docRef = doc(db, 'users', walletAddress)
    const docSnap = await getDoc(docRef)

    if (docSnap.exists()) {
      return docSnap.data() as UserProfile
    }

    return null
  }

  /**
   * Create a new user profile
   */
  async createProfile(walletAddress: string): Promise<UserProfile> {
    const profile: Omit<UserProfile, 'createdAt' | 'lastLoginAt'> & {
      createdAt: ReturnType<typeof serverTimestamp>
      lastLoginAt: ReturnType<typeof serverTimestamp>
    } = {
      walletAddress,
      twitterLinked: false,
      gamesPlayed: 0,
      totalWins: 0,
      totalLosses: 0,
      highestNetWorth: 0,
      preferences: DEFAULT_PREFERENCES,
      createdAt: serverTimestamp(),
      lastLoginAt: serverTimestamp(),
    }

    const docRef = doc(db, 'users', walletAddress)
    await setDoc(docRef, profile)

    // Fetch and return the created profile
    const created = await this.getProfile(walletAddress)
    return created!
  }

  /**
   * Update user profile
   */
  async updateProfile(
    walletAddress: string,
    updates: Partial<Pick<UserProfile, 'username' | 'preferences'>>
  ): Promise<void> {
    const docRef = doc(db, 'users', walletAddress)
    await updateDoc(docRef, {
      ...updates,
      lastLoginAt: serverTimestamp(),
    })
  }

  /**
   * Update last login timestamp
   */
  async updateLastLogin(walletAddress: string): Promise<void> {
    const docRef = doc(db, 'users', walletAddress)
    await updateDoc(docRef, {
      lastLoginAt: serverTimestamp(),
    })
  }

  /**
   * Link Twitter account to user profile
   */
  async linkTwitter(
    walletAddress: string,
    twitterHandle: string
  ): Promise<void> {
    const docRef = doc(db, 'users', walletAddress)
    await updateDoc(docRef, {
      twitterHandle,
      twitterLinked: true,
    })
  }

  /**
   * Unlink Twitter account
   */
  async unlinkTwitter(walletAddress: string): Promise<void> {
    const docRef = doc(db, 'users', walletAddress)
    await updateDoc(docRef, {
      twitterHandle: null,
      twitterLinked: false,
    })
  }

  /**
   * Update game statistics after a game ends
   */
  async updateGameStats(
    walletAddress: string,
    result: 'won' | 'lost',
    finalNetWorth: number
  ): Promise<void> {
    const profile = await this.getProfile(walletAddress)
    if (!profile) return

    const updates: Partial<UserProfile> = {
      gamesPlayed: profile.gamesPlayed + 1,
    }

    if (result === 'won') {
      updates.totalWins = profile.totalWins + 1
    } else {
      updates.totalLosses = profile.totalLosses + 1
    }

    if (finalNetWorth > profile.highestNetWorth) {
      updates.highestNetWorth = finalNetWorth
    }

    const docRef = doc(db, 'users', walletAddress)
    await updateDoc(docRef, updates)
  }

  /**
   * Get user's game history
   */
  async getGameHistory(
    walletAddress: string,
    limitCount: number = 10
  ): Promise<GameSummary[]> {
    const gamesRef = collection(db, 'games')
    const q = query(
      gamesRef,
      where('owner', '==', walletAddress),
      orderBy('startedAt', 'desc'),
      limit(limitCount)
    )

    const snapshot = await getDocs(q)
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as GameSummary[]
  }

  /**
   * Get or create user profile
   */
  async getOrCreateProfile(walletAddress: string): Promise<UserProfile> {
    let profile = await this.getProfile(walletAddress)

    if (!profile) {
      profile = await this.createProfile(walletAddress)
    } else {
      // Update last login
      await this.updateLastLogin(walletAddress)
    }

    return profile
  }

  /**
   * Check if user exists
   */
  async userExists(walletAddress: string): Promise<boolean> {
    const docRef = doc(db, 'users', walletAddress)
    const docSnap = await getDoc(docRef)
    return docSnap.exists()
  }
}

// Export singleton
export const userProfileService = UserProfileService.getInstance()
